<template>
  <div class="card">
    <div class="card-body">
      <event-filter
        :formData="formData"
        :useMetric="useMetric"
        :copy="copy"
        @filter-change="handleFilterChange"
      ></event-filter>
    </div>
  </div>
</template>

<script>
import { createApp, h, markRaw } from 'vue';
import axios from 'axios';
import _ from 'lodash';
import qs from 'qs';

import EventFilter from './event_filter.vue';
import EventResultsPanel from './event_results_panel.vue';
import { terrainFor } from '../../utilities/event_filters';

// Format nested params correctly for GET requests
axios.interceptors.request.use(config => {
  config.paramsSerializer = params => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false
    });
  };
  return config;
});

export default {
  props: [
    'apiEndpoint',
    'initialEvents',
    'initialPagination',
    'initialLocation',
    'formData',
    'useMetric',
    'copy',
    'resultsContainerId'
  ],

  data() {
    return {
      events: this.initialEvents || [],
      pagination: this.transformPagination(this.initialPagination),
      locationDescription: this.initialLocation || '',
      eventIndexHeader: this.initialPagination ? this.initialPagination.header : '',
      isLoading: false,
      activeRequestCancelToken: null,
      cancelTokenFactory: axios.CancelToken,
      filters: this.initialFilters(),
      selectedSort: this.formData.initialSort || 'date_asc',
      resultsApp: null,
      resultsInstance: null,
      shouldIgnoreFilterChanges: false,
      isInitialLoad: true,
      urlUpdateDebounceTimer: null
    };
  },

  components: { EventFilter },

  mounted() {
    this.mountResultsPanel();

    // If we have initial events, ignore filter-change events during initialization
    // After mount completes, allow all filter changes to trigger fetches
    if (this.initialEvents && this.initialEvents.length > 0) {
      this.shouldIgnoreFilterChanges = true;
      this.$nextTick(() => {
        this.shouldIgnoreFilterChanges = false;
        this.isInitialLoad = false;
      });
    } else {
      this.isInitialLoad = false;
    }

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState);
  },

  beforeUnmount() {
    if (this.resultsApp) {
      this.resultsApp.unmount();
    }
    window.removeEventListener('popstate', this.handlePopState);
    if (this.urlUpdateDebounceTimer) {
      clearTimeout(this.urlUpdateDebounceTimer);
    }
  },

  methods: {
    mountResultsPanel() {
      const resultsContainer = document.getElementById(this.resultsContainerId);
      if (!resultsContainer) return;

      const self = this;

      // The results panel renders into its own container elsewhere on the page,
      // so it gets its own app root rather than being part of this component's
      // tree. Props and listeners are flat vnode props (Vue 3 shape) — the old
      // `{ props, on }` form only worked via @vue/compat's RENDER_FUNCTION.
      this.resultsApp = markRaw(createApp({
        render: () => h(EventResultsPanel, {
          events: self.events,
          pagination: self.pagination,
          locationDescription: self.locationDescription,
          eventIndexHeader: self.eventIndexHeader,
          isLoading: self.isLoading,
          selectedSort: self.selectedSort,
          sortOptions: self.formData.sortOptions,
          copy: self.copy,
          useMetric: self.useMetric,
          onSortChange: self.handleSortChange,
          onPageChange: self.handlePaginationClick
        })
      }));
      this.resultsInstance = this.resultsApp.mount(resultsContainer);
    },

    updateResultsPanel() {
      if (!this.resultsInstance) return;

      // Re-render the (separately mounted) results app with the latest data.
      // Its render function reads the live `self.*` values, so forcing a
      // re-render picks up new events/pagination/loading. (Previously this
      // destroyed and re-mounted the instance; under Vue 3 unmount removes the
      // element from the DOM, so re-mounting into the old $el rendered the
      // results into a detached node and they never appeared on the page.)
      this.resultsInstance.$forceUpdate();
    },

    initialFilters() {
      return {
        text: this.formData.initialText || null,
        country: this.formData.initialCountry,
        region: this.formData.initialRegion,
        activity_type: this.formData.initialActivityType || null,
        terrain: null,
        distance: {
          min: 1,
          max: 100,
          min_reached: true,
          max_reached: true
        },
        date_range: {
          start: this.formData.startDate,
          end: this.formData.endDate
        }
      };
    },

    transformPagination(paginationData) {
      if (!paginationData) return null;
      return {
        currentPage: paginationData.currentPage || 1,
        totalPages: paginationData.totalPages || 1
      };
    },

    handleFilterChange(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      if (this.pagination) {
        this.pagination.currentPage = 1;
      }
      // Ignore filter changes during initialization if we have initial events
      if (this.shouldIgnoreFilterChanges) {
        return;
      }
      this.updateUrl();
      this.debouncedFetchEvents();
    },

    handleSortChange(sortValue) {
      this.selectedSort = sortValue;
      this.filters.sort = sortValue;
      if (this.pagination) {
        this.pagination.currentPage = 1;
      }
      this.updateUrl();
      this.debouncedFetchEvents();
    },

    handlePaginationClick(pageNum) {
      this.pagination.currentPage = pageNum;
      this.updateUrl();
      this.fetchEvents();
    },

    debouncedFetchEvents: _.debounce(function() {
      this.fetchEvents();
    }, 400),

    fetchEvents() {
      const self = this;
      self.isLoading = true;
      self.updateResultsPanel();

      // Cancel any currently pending request
      if (self.activeRequestCancelToken) {
        self.activeRequestCancelToken.cancel('Subsequent request made');
      }

      // Generate a cancel token for this request
      self.activeRequestCancelToken = self.cancelTokenFactory.source();

      const params = {
        filters: {
          text: this.filters.text,
          sort: this.selectedSort,
          activity_type: this.filters.activity_type,
          terrain: terrainFor(this.filters.activity_type, this.filters.terrain),
          country: this.filters.country,
          region: this.filters.region,
          distance: this.filters.distance,
          date_range: this.filters.date_range
        },
        page: this.pagination ? this.pagination.currentPage : 1
      };

      axios.get(self.apiEndpoint, {
        headers: { 'Accept': 'application/json' },
        params: params,
        cancelToken: self.activeRequestCancelToken.token
      }).then(response => {
        self.events = response.data.events;
        self.eventIndexHeader = response.data.event_index_header;
        self.locationDescription = response.data.location;
        if (response.data.pagination_data) {
          self.pagination = self.transformPagination(response.data.pagination_data);
        }
        self.activeRequestCancelToken = null;
        self.isLoading = false;
        self.updateResultsPanel();

        // Scroll to results
        const header = document.getElementById('event-index-header');
        if (header) {
          header.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }).catch(function(thrown) {
        if (axios.isCancel(thrown)) {
          console.log(`Request canceled: ${thrown.message}`);
        } else {
          console.error('Error fetching events:', thrown.message);
          self.isLoading = false;
          self.updateResultsPanel();
        }
      });
    },

    updateUrl() {
      // Don't update URL on initial load to avoid duplicate history entry
      if (this.isInitialLoad) {
        return;
      }

      // Debounce URL updates to avoid excessive history entries
      if (this.urlUpdateDebounceTimer) {
        clearTimeout(this.urlUpdateDebounceTimer);
      }

      this.urlUpdateDebounceTimer = setTimeout(() => {
        const params = this.buildUrlParams();
        const queryString = params.toString();
        const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

        // Use replaceState to avoid creating too many history entries
        window.history.replaceState({}, '', newUrl);
      }, 500);
    },

    buildUrlParams() {
      const params = new URLSearchParams();
      const conversionRate = this.useMetric ? 1000 : 1609;

      // Add filters to URL (omit defaults to keep URL clean)
      if (this.filters.text) {
        params.set('text', this.filters.text);
      }

      if (this.filters.country) {
        params.set('country', this.filters.country);
      }

      if (this.filters.region) {
        params.set('region', this.filters.region);
      }

      if (this.filters.activity_type) {
        params.set('activity_type', this.filters.activity_type);
      }

      if (this.filters.terrain) {
        params.set('terrain', this.filters.terrain);
      }

      // Distance: convert from meters back to display units
      if (this.filters.distance) {
        const minDisplay = Math.round(this.filters.distance.min / conversionRate);
        const maxDisplay = Math.round(this.filters.distance.max / conversionRate);

        // Only add if not default (1-100 with both reached)
        if (!(minDisplay === 1 && maxDisplay === 100 &&
              this.filters.distance.min_reached && this.filters.distance.max_reached)) {
          params.set('distance_min', this.filters.distance.min);
          params.set('distance_max', this.filters.distance.max);
          if (this.filters.distance.min_reached) {
            params.set('distance_min_reached', 'true');
          }
          if (this.filters.distance.max_reached) {
            params.set('distance_max_reached', 'true');
          }
        }
      }

      // Date range
      if (this.filters.date_range && this.filters.date_range.start) {
        params.set('start_date', this.filters.date_range.start);
      }
      if (this.filters.date_range && this.filters.date_range.end) {
        params.set('end_date', this.filters.date_range.end);
      }

      // Sort (omit if default)
      if (this.selectedSort && this.selectedSort !== 'date_asc') {
        params.set('sort', this.selectedSort);
      }

      // Page (omit if page 1)
      if (this.pagination && this.pagination.currentPage > 1) {
        params.set('page', this.pagination.currentPage);
      }

      return params;
    },

    handlePopState(event) {
      // Handle browser back/forward navigation
      // Parse URL parameters and update filters
      const urlParams = new URLSearchParams(window.location.search);
      const activityType = urlParams.get('activity_type') || null;

      // Update filters from URL
      this.filters = {
        text: urlParams.get('text') || null,
        country: urlParams.get('country') || this.formData.initialCountry,
        region: urlParams.get('region') || '',
        activity_type: activityType,
        terrain: terrainFor(activityType, urlParams.get('terrain')),
        distance: {
          min: parseInt(urlParams.get('distance_min')) || 1000,
          max: parseInt(urlParams.get('distance_max')) || 100000,
          min_reached: urlParams.get('distance_min_reached') === 'true',
          max_reached: urlParams.get('distance_max_reached') === 'true'
        },
        date_range: {
          start: urlParams.get('start_date') || this.formData.startDate,
          end: urlParams.get('end_date') || this.formData.endDate
        }
      };

      this.selectedSort = urlParams.get('sort') || 'date_asc';

      if (this.pagination) {
        this.pagination.currentPage = parseInt(urlParams.get('page')) || 1;
      }

      // Fetch events with new filters
      this.fetchEvents();
    }
  }
};
</script>
